// Performance monitoring utility for mobile optimizations
import videoOptimizer from './videoOptimizer';

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.init();
  }

  init() {
    if (this.isMobile) {
      this.setupMobileMonitoring();
    }
    this.setupCoreWebVitals();
    this.setupCustomMetrics();
    this.setupVideoOptimizationTracking();
  }

  setupVideoOptimizationTracking() {
    // Track video optimization impact
    if (this.isMobile) {
      setTimeout(() => {
        const videoStats = videoOptimizer.getStats();
        this.metrics.videoOptimization = videoStats;
        
        if (videoStats.optimizedVideos > 0) {
          console.log(`🎥 Video optimization: ${videoStats.optimizedVideos}/${videoStats.totalVideos} videos optimized for mobile`);
          
          // Track to analytics
          if (window.mixpanel) {
            window.mixpanel.track('Video Optimization Applied', {
              totalVideos: videoStats.totalVideos,
              optimizedVideos: videoStats.optimizedVideos,
              isMobile: this.isMobile,
              timestamp: Date.now()
            });
          }
        }
      }, 2000); // Wait for video optimizer to process existing videos
    }
  }

  setupMobileMonitoring() {
    // Monitor memory usage on mobile
    if ('memory' in performance) {
      setInterval(() => {
        this.metrics.memory = {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit
        };
      }, 5000);
    }

    // Monitor connection quality
    if ('connection' in navigator) {
      const connection = navigator.connection;
      this.metrics.connection = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      };

      connection.addEventListener('change', () => {
        this.metrics.connection = {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt
        };
      });
    }

    // Monitor battery status if available
    if ('getBattery' in navigator) {
      navigator.getBattery().then(battery => {
        this.metrics.battery = {
          level: battery.level,
          charging: battery.charging
        };

        battery.addEventListener('levelchange', () => {
          this.metrics.battery.level = battery.level;
        });

        battery.addEventListener('chargingchange', () => {
          this.metrics.battery.charging = battery.charging;
        });
      });
    }
  }

  setupCoreWebVitals() {
    // First Contentful Paint
    if ('PerformanceObserver' in window) {
      try {
        const paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              this.metrics.fcp = entry.startTime;
              this.logMetric('FCP', entry.startTime);
            }
          }
        });
        paintObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('FCP monitoring failed:', e);
      }

      // Largest Contentful Paint
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.metrics.lcp = entry.startTime;
            this.logMetric('LCP', entry.startTime);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP monitoring failed:', e);
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.metrics.fid = entry.processingStart - entry.startTime;
            this.logMetric('FID', this.metrics.fid);
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.warn('FID monitoring failed:', e);
      }

      // Cumulative Layout Shift
      try {
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
          this.metrics.cls = clsValue;
          this.logMetric('CLS', clsValue);
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('CLS monitoring failed:', e);
      }
    }
  }

  setupCustomMetrics() {
    // Time to Interactive
    if ('PerformanceObserver' in window) {
      try {
        const ttiObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'TTI') {
              this.metrics.tti = entry.startTime;
              this.logMetric('TTI', entry.startTime);
            }
          }
        });
        ttiObserver.observe({ entryTypes: ['measure'] });
      } catch (e) {
        console.warn('TTI monitoring failed:', e);
      }
    }

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) { // Tasks longer than 50ms
              this.logMetric('LongTask', entry.duration);
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        console.warn('Long task monitoring failed:', e);
      }
    }
  }

  logMetric(name, value) {
    const isGood = this.isMetricGood(name, value);
    const emoji = isGood ? '✅' : '⚠️';
    
    console.log(`${emoji} ${name}: ${this.formatValue(name, value)}`);
    
    // Send to analytics if available
    if (window.mixpanel) {
      window.mixpanel.track('Performance Metric', {
        metric: name,
        value: value,
        isMobile: this.isMobile,
        isGood: isGood,
        timestamp: Date.now()
      });
    }
  }

  isMetricGood(name, value) {
    const thresholds = {
      'FCP': { good: 1800, poor: 3000 },
      'LCP': { good: 2500, poor: 4000 },
      'FID': { good: 100, poor: 300 },
      'CLS': { good: 0.1, poor: 0.25 },
      'TTI': { good: 3800, poor: 7300 }
    };

    const threshold = thresholds[name];
    if (!threshold) return true;

    return value <= threshold.good;
  }

  formatValue(name, value) {
    if (name === 'CLS') {
      return value.toFixed(3);
    } else if (['FCP', 'LCP', 'TTI', 'FID'].includes(name)) {
      return `${Math.round(value)}ms`;
    }
    return value;
  }

  getMetrics() {
    return this.metrics;
  }

  getPerformanceScore() {
    let score = 100;
    let totalMetrics = 0;

    if (this.metrics.fcp) {
      totalMetrics++;
      if (this.metrics.fcp > 3000) score -= 20;
      else if (this.metrics.fcp > 1800) score -= 10;
    }

    if (this.metrics.lcp) {
      totalMetrics++;
      if (this.metrics.lcp > 4000) score -= 25;
      else if (this.metrics.lcp > 2500) score -= 15;
    }

    if (this.metrics.fid) {
      totalMetrics++;
      if (this.metrics.fid > 300) score -= 20;
      else if (this.metrics.fid > 100) score -= 10;
    }

    if (this.metrics.cls) {
      totalMetrics++;
      if (this.metrics.cls > 0.25) score -= 20;
      else if (this.metrics.cls > 0.1) score -= 10;
    }

    return totalMetrics > 0 ? Math.max(0, score) : 100;
  }

  generateReport() {
    const score = this.getPerformanceScore();
    const report = {
      score,
      grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F',
      metrics: this.metrics,
      isMobile: this.isMobile,
      timestamp: Date.now(),
      recommendations: this.getRecommendations()
    };

    console.log('🚀 Performance Report:', report);
    return report;
  }

  getRecommendations() {
    const recommendations = [];
    
    if (this.metrics.fcp > 3000) {
      recommendations.push('Optimize First Contentful Paint: Reduce render-blocking resources');
    }
    
    if (this.metrics.lcp > 4000) {
      recommendations.push('Optimize Largest Contentful Paint: Optimize images and critical resources');
    }
    
    if (this.metrics.fid > 300) {
      recommendations.push('Reduce First Input Delay: Break up long tasks and optimize JavaScript');
    }
    
    if (this.metrics.cls > 0.25) {
      recommendations.push('Reduce Cumulative Layout Shift: Set explicit dimensions for images and ads');
    }

    if (this.isMobile && this.metrics.memory?.used > 50 * 1024 * 1024) {
      recommendations.push('Reduce memory usage on mobile devices');
    }

    // Add video optimization recommendations
    if (this.metrics.videoOptimization) {
      const { totalVideos, optimizedVideos } = this.metrics.videoOptimization;
      if (totalVideos > 0 && optimizedVideos < totalVideos) {
        recommendations.push(`Video optimization: ${optimizedVideos}/${totalVideos} videos optimized for mobile`);
      }
    }

    return recommendations;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;
